import React, { useMemo, useContext } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { PAYMENT_CARD_STYLES } from '../../common/cardStyleConstants';
import { AvatarCircle } from '@/components/common/AvatarCircle';
import { RNButton } from '@/components';
import { PaymentContent } from '../shared/PaymentContent';
import { RecurringCharges } from '@/features/payments/components/shared/RecurringCharges';
import { PaymentSummaryContext } from '../../context/PaymentSummaryContext';
import { useAppSelector } from '@/store';
import { useGetConfigurationStatusQuery } from '@/store/api';
import { getIsOverdue, getInCredit, mapLeaseStatus } from '@/features/payments/common/helpers';
import { EntrataDisabledPaymentContent } from '../shared/EntrataDisabledPaymentContent';
import { useEntrataIntegrationDisabled } from '@/features/payments/hooks/useEntrataIntegrationDisabled';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { LeasePill } from '../shared/LeasePill';
import { MakePaymentBtn } from '../shared/MakePaymentBtn';
import { ChargeBreakdown } from '../shared/ChargeBreakdown';
import type { BalanceItem } from '@/types/residents/lease';

interface Props {
  leaseId: number;
  isSelected?: boolean;
  balanceDue?: number;
  balanceItems?: BalanceItem[];
  propertyMarketingName?: string;
  paymentsBlocked?: boolean;
  autopayEnabled: boolean;
  scheduledCharges: any[];
  statusType?: string;
  users?: any[];
  unitNumberSpace?: string;
  propertyId?: number;
  isConnection: boolean;
  onRefresh?: () => void;
}

export const AsyncChargesSummaryCard: React.FC<Props> = ({
  leaseId,
  isSelected = false,
  balanceDue = 0,
  balanceItems,
  propertyMarketingName = '',
  paymentsBlocked = false,
  autopayEnabled,
  scheduledCharges,
  statusType,
  users,
  unitNumberSpace,
  propertyId,
}) => {
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const connectedPrimary = useMemo(() => {
    return users?.find((u: any) => u?.id !== authUser?.id && u?.LeaseCustomer?.customerType === 'primary');
  }, [users, authUser]);

  const displayUser = connectedPrimary || users?.find((u: any) => u?.id !== authUser?.id) || users?.[0] || {};

  const isOverdue = getIsOverdue(balanceDue);
  const inCredit = getInCredit(balanceDue);
  const shouldPay = (balanceDue && !inCredit) || isOverdue;

  const tableData: (string | number)[][] = useMemo(() => {
    if (!Array.isArray(scheduledCharges)) return [];
    return scheduledCharges.map((c: any) => [c.chargeCode || c.description || 'Charge', c.amount ? `$${c.amount}` : 'TBD', 'Monthly']);
  }, [scheduledCharges]);

  let bannerMsg = '';
  if (paymentsBlocked) {
    bannerMsg = 'No payments are being accepted at this time. Please contact your community manager if you have questions.';
  } else if (isOverdue) {
    bannerMsg = 'This account has overdue charges. Payments are due on the first of each month.';
  } else if (shouldPay) {
    bannerMsg = 'This account has outstanding charges. Payments are due on the first of each month.';
  } else if ((balanceDue || 0) === 0) {
    bannerMsg = 'Great! This account has no outstanding charges.';
  } else if (inCredit) {
    bannerMsg = 'This account has a credit. You can always pay ahead to cover future charges.';
  }

  const { setCurrentlyDisplayedLeaseId } = useContext(PaymentSummaryContext);

  const { data: configData } = useGetConfigurationStatusQuery(propertyId as number, { skip: !propertyId });
  const entrataDisabled = useEntrataIntegrationDisabled();

  const rightSide = (
    <>
      {entrataDisabled ? (
        <EntrataDisabledPaymentContent propertyId={propertyId} />
      ) : (
        <PaymentContent
          leaseId={leaseId}
          paymentsBlocked={paymentsBlocked}
          isOverdue={isOverdue}
          inCredit={inCredit}
          autopayEnabled={autopayEnabled}
          balanceDue={balanceDue}
        />
      )}
      {!entrataDisabled && !paymentsBlocked && (
        <MakePaymentBtn
          leaseId={leaseId}
          residentId={displayUser?.id}
          leaseUser={connectedPrimary}
          variant={shouldPay ? (isOverdue ? 'primary' : 'secondary') : 'outlined'}
          style={styles.payBtn}
          textStyle={shouldPay ? undefined : styles.payBtnText}
        />
      )}
    </>
  );

  const header = (
    <View style={styles.headerRow}>
      <View style={styles.headerLeftWrap}>
        <AvatarCircle
          firstName={displayUser?.firstName}
          lastName={displayUser?.lastName}
          size={62}
          style={[styles.avatar, { backgroundColor: RESIDENTS_DESKTOP_COLORS.CIRCLE_ICON_BACKGROUND }]}
        />
        <View style={styles.headerTextCol}>
          <LeasePill leaseStatus={mapLeaseStatus(statusType)} />
          <Text style={styles.headerTitle} numberOfLines={1}>
            {displayUser?.firstName && displayUser?.lastName
              ? `${displayUser.firstName} ${displayUser.lastName}`
              : 'Resident'}
          </Text>
          <Text style={styles.headerSub} numberOfLines={1}>
            {propertyMarketingName}
            {unitNumberSpace ? ` • ${unitNumberSpace}` : ''}
          </Text>
          <Text style={styles.headerMsg} numberOfLines={2}>{bannerMsg}</Text>
        </View>
      </View>
      <View style={styles.rightCol}>{rightSide}</View>
    </View>
  );

  const inner = (
    <View>
      {header}
      {!isSelected && !entrataDisabled && (
        <View style={styles.footerRow}>
          <View style={styles.horizontalRule} />
          <View style={styles.seeDetailsRow}>
            <RNButton
              title="See Details"
              variant="link"
              style={styles.seeDetailsLink}
              textStyle={styles.seeDetailsLinkText}
              icon={<ChevronRightIcon />}
              iconPosition="right"
              onPress={() => setCurrentlyDisplayedLeaseId(leaseId)}
            />
          </View>
        </View>
      )}
      {isSelected && balanceItems && balanceItems.length > 0 && !paymentsBlocked && (
        <ChargeBreakdown
          balanceItems={balanceItems}
          balanceDue={balanceDue}
          variant="desktop"
          style={styles.chargesSpacing}
        />
      )}
      {(isSelected || entrataDisabled) && tableData.length > 0 && !paymentsBlocked && (
        <View style={styles.bodyRow}>
          <View style={styles.leftCol}>
            <RecurringCharges tableData={tableData} />
          </View>
        </View>
      )}
    </View>
  );

  const card = (
    <View style={[styles.card, isOverdue ? styles.cardOverdue : undefined]}>
      {isOverdue && (
        <View style={styles.overdueBanner}>
          <Text style={styles.overdueText}>
            {connectedPrimary ? `${displayUser?.firstName} ${displayUser?.lastName}’s balance is past due!` : 'Your balance is past due!'}
          </Text>
        </View>
      )}
      {inner}
    </View>
  );

  return card;
};

const HouseIcon: React.FC = () => {
  const Icon = RESIDENTS_SVG_COMPONENTS.HOUSE as any;
  if (!Icon) return null;
  return <Icon width={14} height={14} color={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY} />;
};

const ChevronRightIcon: React.FC = () => {
  const Icon = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT as any;
  if (!Icon) return null;
  return <Icon width={14} height={14} color={RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY} />;
};

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 8,
    marginBottom: PAYMENT_CARD_STYLES.MARGIN_BOTTOM,
    borderRadius: 12,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    paddingVertical: PAYMENT_CARD_STYLES.PADDING_VERTICAL,
    minHeight: PAYMENT_CARD_STYLES.MIN_HEIGHT,
  },
  cardOverdue: {
    borderColor: '#EF4444',
    borderWidth: 1,
  },
  overdueBanner: {
    backgroundColor: '#EF4444',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  overdueText: {
    color: '#111827',
    fontSize: 12,
    fontWeight: '600',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  headerLeftWrap: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
    paddingRight: 12,
  },
  avatar: {
    marginRight: 12,
  },
  headerTextCol: {
    flex: 1,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.08)',
    marginBottom: 8,
  },
  badgeText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 12,
    fontWeight: '700',
    marginLeft: 6,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  headerSub: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 6,
  },
  headerMsg: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
  bodyRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  footerRow: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  leftCol: {
    flex: 1,
    paddingRight: 12,
  },
  rightCol: {
    width: 240,
    alignItems: 'flex-end',
  },
  verticalDivider: {
    position: 'absolute',
    left: -12,
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  seeDetailsLink: {
    alignSelf: 'flex-start',
    paddingTop: 8,
  },
  seeDetailsLinkText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
  },
  seeDetailsRow: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  horizontalRule: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.08)',
    marginTop: 12,
  },
  payBtn: {
    borderBottomWidth: 1,
    borderBottomColor: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
    marginTop: 8,
  },
  payBtnText: {
    color: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  chargesSpacing: {
    marginHorizontal: 16,
    marginTop: 4,
    marginBottom: 12,
  },
});


